// GA4 events. gtag is injected in index.html and is deliberately absent on staging,
// on localhost and on account pages — so every call here has to survive it missing.
// ponytail: no wrapper layer, no queue, no consent plumbing — one function or nothing.

type Gtag = (command: string, ...args: unknown[]) => void;

const gtag = (): Gtag | undefined => (window as unknown as { gtag?: Gtag }).gtag;

/** Send one GA4 event. No-op wherever the tag is not installed. */
export const track = (event: string, params?: Record<string, unknown>): void => {
  gtag()?.('event', event, params);
};

/** Manual page_view: index.html configures the tag with send_page_view: false. */
export const trackPageView = (): void => {
  gtag()?.('event', 'page_view', {
    page_location: window.location.href,
    page_title: document.title
  });
};

/**
 * One page_view for the route that just rendered; returns the cleanup for the effect.
 *
 * Helmet commits <title> inside a requestAnimationFrame, so waiting one frame reports
 * the new title instead of the previous one. A hidden tab runs no frames at all — a tab
 * opened in the background and closed before it was ever looked at would then never be
 * counted, so there the hit goes out at once and carries the title index.html shipped.
 */
export const schedulePageView = (): (() => void) => {
  if (document.hidden) {
    trackPageView();
    return () => {};
  }
  const frame = requestAnimationFrame(trackPageView);
  return () => cancelAnimationFrame(frame);
};

// Conversion errors reach the frontend as the worker's raw Error.message (server:
// queue/async-queue.ts stores error.message verbatim), which can carry the uploaded
// file name, a temp path or a LibreOffice command line. None of that may go to GA4,
// and free text would blow up the cardinality of the parameter as well. Only the
// categories below are ever sent.
const ERROR_CATEGORIES: [RegExp, string][] = [
  [/timed out|timeout/i, 'timeout'],
  [/cancel/i, 'cancelled'],
  [/network/i, 'network'],
  [/not found/i, 'not_found'],
  [/no file uploaded/i, 'no_file'],
  [/internal server error/i, 'server_error'],
  [/^conversion failed$/i, 'conversion_failed']
];

/** Stable, allowlisted label for a failure; anything unrecognised becomes 'other'. */
export const errorCategory = (message: string): string =>
  ERROR_CATEGORIES.find(([pattern]) => pattern.test(message))?.[1] ?? 'other';

/** One decimal is enough to bucket file sizes in reports. */
export const sizeMb = (bytes: number): number => Number((bytes / 1024 / 1024).toFixed(1));

// --- Consent Mode v2 ---------------------------------------------------------
// The default ("denied", or the stored answer of a returning visitor) is set in
// index.html before the first hit. Here we only handle the answer given in the banner.

/** Same key as in index.html — the HTML tag cannot import it. */
const CONSENT_KEY = 'scormate-analytics-consent';

/** True only where the tag is actually installed: prod, outside account pages. */
export const hasAnalyticsTag = (): boolean => Boolean(gtag());

/**
 * Countries where storage stays denied until the visitor answers: the EEA, the UK and
 * Switzerland. Google applies the same list by IP in index.html — consent-tag.test.ts
 * keeps the two copies in step, since an inline tag cannot import this one.
 */
export const CONSENT_REGIONS = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS',
  'LI', 'NO', 'GB', 'CH'
];

/**
 * The visitor's country as Cloudflare resolved it from the IP address, or null when the
 * edge did not answer. This is the signal Google itself uses for the regional default,
 * so asking it here keeps the banner and the default from disagreeing.
 */
export const edgeCountry = async (): Promise<string | null> => {
  try {
    const response = await fetch('/cdn-cgi/trace', { cache: 'no-store' });
    if (!response.ok) return null;
    return /(?:^|\n)loc=([A-Z]{2})\b/.exec(await response.text())?.[1] ?? null;
  } catch {
    return null; // offline, blocked, or no Cloudflare in front: fall back to the time zone
  }
};

/**
 * Fallback for a visitor whose country the edge would not tell us: index.html guesses
 * from the time zone and leaves the flag behind. It errs towards asking.
 */
export const consentRequired = (): boolean =>
  Boolean((window as unknown as { __scormateConsentAsk?: boolean }).__scormateConsentAsk);

/** 'granted' | 'denied', or null while the visitor has not answered. */
export const storedConsent = (): 'granted' | 'denied' | null => {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === 'granted' || value === 'denied' ? value : null;
  } catch {
    return null; // private mode: treat as unanswered, the default stays denied
  }
};

/** Remember the answer and apply it to the running tag. */
export const setConsent = (granted: boolean): void => {
  const value = granted ? 'granted' : 'denied';
  try {
    localStorage.setItem(CONSENT_KEY, value);
  } catch {
    // Storage blocked: the answer holds for this page load only.
  }
  gtag()?.('consent', 'update', { analytics_storage: value });
};
